import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business,
  ArrowForward,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import '../styles/auth.css';

function CompleteRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    tenantName: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Get the Google user data from location state
  const googleUser = location.state?.googleUser;

  useEffect(() => {
    // If no Google user data or already has a tenant, redirect to dashboard
    if (!googleUser || (isAuthenticated && !location.state?.needsTenantSetup)) {
      navigate('/dashboard', { replace: true });
    }
  }, [googleUser, isAuthenticated, navigate, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;
    
    setError('');
    
    // Validate form
    if (!formData.tenantName.trim()) {
      setError('Company name is required');
      return;
    }
    
    setIsLoading(true);

    try {
      // Call API to complete Google registration
      const response = await fetch('/api/auth/complete-google-registration', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          tenantName: formData.tenantName
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete registration');
      }
      
      const data = await response.json();
      
      // Update local storage with tenant info
      localStorage.setItem('tenant', JSON.stringify(data.tenant));
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Redirect to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Complete registration error:', err);
      setError(err.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modern-auth-container">
      <div className="bg-circle bg-circle-1"></div>
      <div className="bg-circle bg-circle-2"></div>
      <div className="bg-circle bg-circle-3"></div>
      <div className="modern-auth-card">
        <div className="auth-header">
          <h1>Complete Your Registration</h1>
          <p>Just one more step to set up your Eazy Fatoora account</p>
        </div>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="tenantName">Company name</label>
            <div className="input-container">
              <Business className="input-icon" />
              <input
                type="text"
                id="tenantName"
                name="tenantName"
                placeholder="Your company"
                value={formData.tenantName}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          <button 
            type="submit" 
            className="signin-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <>
                Complete Setup
                <ArrowForward className="button-icon" />
              </>
            )}
          </button>
          
          <div className="signup-link">
            <p>Welcome, {googleUser?.username || 'User'}! Please enter your company name to complete your registration.</p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CompleteRegistration;
